// The notification bell in the top navigation.
//
// Opens on hover AND on click, because the two are different intents: hovering is "is there
// anything?", clicking is "I am going to deal with this". A hover-only menu cannot be used
// on a touch screen; a click-only one makes you commit to find out there is nothing.
//
// The unread count is server-rendered into the island payload, so the bell is the right
// colour on first paint rather than changing a round trip later. The list itself is fetched
// when the menu first opens — most page loads never open it.
//
// ## Keeping it current
//
// The payload is a snapshot of page-load time, so a tab left open all day used to show the
// count it had at breakfast. It is re-polled once a minute — with `unreadCount`, which is one
// indexed COUNT, not `list`, which fetches thirty rows and joins `User` for each actor. The
// bell is shown to every logged-in user, so the difference is the whole site's worth of
// polling, not eight admins'.
//
// Polling PAUSES while the tab is hidden and fires once immediately when it comes back: a
// backgrounded tab asking every minute for hours costs real queries to tell nobody anything,
// and what a returning reader wants is the count now, not up to a minute from now.
//
// Clicking the bell marks everything read, so the disc goes from red back to grey. Hovering
// does not: see the `onClick` note below for why the two differ.
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { Islands } from "@brave-new-world/shared";
import { mountIsland } from "../ui/islands";

/**
 * How often an open tab re-checks.
 *
 * A minute: a notification is not a chat message, and this runs for every logged-in user
 * with a tab open.
 */
const POLL_MS = 60_000;

/**
 * How long opening a notification will wait for its read receipt before navigating anyway.
 *
 * A receipt is one indexed `UPDATE` and normally answers in tens of milliseconds, so this is
 * a ceiling for when something is wrong, not a budget anybody spends.
 */
const MARK_WAIT_MS = 1_000;
import { api } from "../api";
import { IMG_ROOT } from "../lib/constants";

type Item = Awaited<ReturnType<typeof api.notifications.list>>["items"][number];

/** "just now" / "14m" / "3h" / "2d" — a bell wants age, not a timestamp. */
function age(at: number, now: number): string {
  const s = Math.max(0, Math.floor((now - at) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)}h`;
  return `${String(Math.floor(h / 24))}d`;
}

function NotificationBell({ unread: seeded }: { unread: number }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(seeded);

  const [items, setItems] = useState<Item[] | null>(null);
  const box = useRef<HTMLDivElement>(null);
  /** Cancels the close when the pointer crosses the gap between the bell and the menu. */
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /**
   * Whether this page has already marked everything read.
   *
   * ⚠️ Load-bearing against a race, not a cache. Clicking the bell marks read and fetches
   * the list at the same time, and the two are separate round trips — so the list can be
   * answered from before the update committed and come back still saying "3 unread". Without
   * this the disc flashes grey and then back to red a moment later, which looks exactly like
   * the mark having failed.
   */
  const cleared = useRef(false);

  /**
   * Mark-read requests that have been sent but not yet answered.
   *
   * ⚠️ Load-bearing, and the reason this component cannot fire-and-forget a read receipt
   * before navigating. `lib/server.ts` issues plain `fetch` calls with no `keepalive`, so the
   * browser ABORTS any that are still open when a navigation starts tearing the document
   * down. Opening a notification does both at once — mark it read, then go to its link — and
   * the POST lost that race essentially every time, which is why clicking a notification
   * appeared to do nothing and the bell was red again on the next page.
   */
  const pending = useRef<Promise<unknown>[]>([]);

  /** Send a mark-read and keep it in `pending` until it settles. Never rejects. */
  const mark = useCallback((id: string | null): Promise<unknown> => {
    const sent = api.notifications.markRead({ id }).catch(() => undefined);
    pending.current = [...pending.current, sent];
    void sent.then(() => {
      pending.current = pending.current.filter((p) => p !== sent);
    });
    return sent;
  }, []);

  /**
   * Whether a list fetch is already out.
   *
   * The dedupe is what makes it safe to reload on every open: the pointer leaving and
   * returning, or a poll firing into an open menu, must not stack requests.
   */
  const loading = useRef(false);

  /**
   * `open`, readable from the poll, and written in the same breath as the state.
   *
   * The poll lives in an interval created once; closing over `open` would either capture the
   * value at mount or force the interval to be torn down and rebuilt on every toggle, which
   * resets its phase and is how a "once a minute" timer quietly becomes "never" for someone
   * fiddling with the menu.
   *
   * Written through `setMenuOpen` rather than synced by an effect, so it is correct the
   * instant the menu opens instead of one deferred callback later.
   */
  const openRef = useRef(false);

  /** The only way the menu's openness changes — keeps state and ref from drifting apart. */
  const setMenuOpen = useCallback((next: boolean): void => {
    openRef.current = next;
    setOpen(next);
  }, []);

  /**
   * Fetch the rows.
   *
   * The previous rows stay on screen until the new ones arrive — `setItems` is only called
   * with the answer, never with `null` first. That is what keeps a reload from flashing
   * "Loading…" over a list the reader is already looking at; the placeholder is for the very
   * first fetch, when there is genuinely nothing to show.
   */
  const load = useCallback(() => {
    if (loading.current) return;
    loading.current = true;
    void api.notifications
      .list(undefined)
      .then((r) => {
        setItems(r.items);
        // A local mark-read wins over an answer that may predate it.
        setUnread(cleared.current ? 0 : r.unread);
      })
      .catch(() => undefined)
      .finally(() => {
        loading.current = false;
      });
  }, []);

  /**
   * Mark everything read.
   *
   * `keepHighlights` is what separates opening the menu from pressing the button. Opening it
   * clears the BELL but leaves the rows marked, so you can still see which ones were new —
   * losing that the instant the menu opens would make the highlight useless. Pressing "Mark
   * all read" is an explicit "I am done with these", and clears both.
   */
  const markAllRead = useCallback(
    (keepHighlights: boolean) => {
      cleared.current = true;
      setUnread(0);
      if (!keepHighlights) {
        setItems((cur) => cur?.map((i) => ({ ...i, read: true })) ?? cur);
      }
      // Not awaited here and not surfaced: the bell has already gone grey, and an error toast
      // over a failed read-receipt is worse than the receipt being retried next time. It is
      // tracked rather than dropped, though, so a click on a notification a moment later waits
      // for it instead of navigating out from under it.
      void mark(null);
    },
    [mark]
  );

  /**
   * The count in the browser tab's title: `[3] Pictufy`.
   *
   * Which is the only part of this a reader sees while looking at something else — the bell
   * is on the page they are not on.
   *
   * ⚠️ The base is read from the LIVE title every time and any existing `[n] ` stripped, not
   * captured once at mount. Two reasons, and the second is why a cached base is wrong rather
   * than merely stale: the dashboard is a client-side SPA that rewrites the title as you move
   * between its tabs, so re-applying a remembered base would overwrite whatever it had just
   * set. Reading it fresh means this only ever adds or removes its own prefix, and the next
   * poll heals a prefix some other writer dropped.
   */
  const applyTitle = useCallback((n: number): void => {
    const base = document.title.replace(/^\[\d+\]\s*/, "");
    document.title = n > 0 ? `[${String(n)}] ${base}` : base;
  }, []);

  /** Ask for the number alone. Never throws: a failed poll keeps the last known count. */
  const pollUnread = useCallback(() => {
    void api.notifications
      .unreadCount(undefined)
      .then((r) => {
        // A local mark-read still wins, for the same reason `load` defers to it.
        const next = cleared.current ? 0 : r.unread;
        setUnread(next);
        // Re-asserted on every poll, not only when the number changes: something else may
        // have rewritten the title since, and an unchanged count re-renders nothing.
        applyTitle(next);
        // Menu up while something arrived: the rows behind it are now wrong too. Opening it
        // refetches, but nobody closes and reopens a menu they are already reading.
        if (openRef.current && r.unread > 0) load();
      })
      .catch(() => undefined);
  }, [applyTitle, load]);

  useEffect(() => {
    const tick = (): void => {
      if (document.visibilityState === "hidden") return;
      pollUnread();
    };
    const timer = setInterval(tick, POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") pollUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pollUnread]);

  useEffect(() => {
    applyTitle(unread);
  }, [unread, applyTitle]);

  /**
   * Bring the menu up, with the rows the server has NOW.
   *
   * ⚠️ Fetched on every closed→open transition. It used to be `if (items === null) load()`,
   * which reads as "once per open" and is in fact once per PAGE: a tab left open showed the
   * list as it stood the first time the menu was ever opened, so a notification that arrived
   * afterwards lit the bell and then was not in the menu the bell was pointing at. Only a
   * reload fixed it — which is a bad bargain for a feature whose whole job is to tell you
   * something arrived.
   *
   * Reloading is cheap enough to do unconditionally: `loading` collapses repeats, the open is
   * a deliberate act rather than a timer, and the old rows stay up while the new ones land.
   * A hover-in while the menu is already open does nothing, so sliding the pointer around
   * inside it costs nothing either.
   */
  const show = useCallback(() => {
    clearTimeout(closeTimer.current);
    if (!open) load();
    setMenuOpen(true);
  }, [open, load, setMenuOpen]);

  // A small delay so the pointer can travel from the bell to the menu without it vanishing.
  const hide = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setMenuOpen(false);
    }, 250);
  }, [setMenuOpen]);

  /**
   * Cancel the close timer if the bell goes away before it fires.
   *
   * ⚠️ The one timer here that had no unmount cleanup — the poll interval and the
   * document listeners both have one. It was missed because this timer is armed from an
   * event handler rather than from an effect, so there was no cleanup function sitting
   * next to it asking to be written.
   *
   * Left alone, it calls `setMenuOpen` on an unmounted component up to 250 ms later, which
   * schedules a render and then a deferred effect flush. In a browser that is merely
   * wasteful — the bell lives as long as the page. Under a test runner the environment is
   * already gone by then, and the flush dies on `document is not defined`, blaming
   * whichever test happened to be running.
   */
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Clicking elsewhere closes it — the usual expectation for a menu, and the only way out on
  // a touch screen where there is no "pointer leaves".
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setMenuOpen]);

  const now = Date.now();

  /**
   * Open a notification: mark it read, then follow its link.
   *
   * The order on screen is immediate — the row loses its highlight and the count drops before
   * anything is awaited — but the NAVIGATION waits for the receipts to land. It has to: see
   * the note on `pending` above for why a navigation and an open fetch cannot coexist.
   *
   * Capped by `MARK_WAIT_MS` so a slow or dead server costs a moment rather than the link.
   * Losing the receipt is a wrong bell; losing the navigation is a notification you cannot
   * open, which is worse.
   */
  const openItem = (item: Item): void => {
    const waitFor = [...pending.current];
    if (!item.read) {
      waitFor.push(mark(item.id));
      setUnread((n) => Math.max(0, n - 1));
      setItems((cur) => cur?.map((i) => (i.id === item.id ? { ...i, read: true } : i)) ?? cur);
    }

    const link = item.link;
    if (!link) return;
    if (waitFor.length === 0) {
      location.href = link;
      return;
    }
    void Promise.race([
      Promise.all(waitFor),
      new Promise((resolve) => setTimeout(resolve, MARK_WAIT_MS)),
    ]).then(() => {
      location.href = link;
    });
  };

  return (
    <div class="notif" ref={box} onMouseEnter={show} onMouseLeave={hide}>
      <span
        class={
          `notif-bell${unread > 0 ? " notif-bell-unread" : ""}` +
          // Not left to `:hover`: the menu stays open while the pointer is inside IT, at
          // which point the pointer is off the disc and a hover-only rule would send it
          // back to grey with its own menu still hanging off it.
          (open ? " notif-bell-open" : "")
        }
        title={unread > 0 ? `${String(unread)} unread` : "Notifications"}
        onClick={() => {
          if (open) {
            setMenuOpen(false);
            return;
          }
          show();
          // Click only — NOT hover. A pointer crossing the nav on its way somewhere else
          // would otherwise clear the bell for a notification nobody looked at, which is the
          // "every unseen row marked seen by the page that rendered it" mistake the legacy
          // toast table makes. Clicking is an act; hovering is a glance.
          if (unread > 0) markAllRead(true);
        }}
      >
        {/* No count bubble: the circle itself turns red, and a 16px badge on a 25px disc is
            two ways of saying one thing in the space of one. The exact number is in the
            `title` above, and in the menu this opens. */}
        <i class="far fa-bell" />
      </span>

      {open && (
        <div class="notif-menu">
          <div class="notif-menu-head">
            <b>Notifications</b>
            {unread > 0 && (
              <span
                class="notif-markall"
                onClick={() => {
                  markAllRead(false);
                }}
              >
                Mark all read
              </span>
            )}
          </div>

          {items === null && <div class="notif-empty">Loading…</div>}
          {items !== null && items.length === 0 && <div class="notif-empty">Nothing yet.</div>}
          {items?.map((item) => (
            <div
              key={item.id}
              class={`notif-item${item.read ? "" : " notif-item-unread"}`}
              onClick={() => {
                openItem(item);
              }}
            >
              {item.actorThumb ? (
                <img class="notif-face" src={`${IMG_ROOT}${item.actorThumb}`} alt="" />
              ) : (
                <div class="notif-face notif-face-empty" />
              )}
              <div class="notif-item-body">
                <div class="notif-item-title">{item.title}</div>
                {item.body && <div class="notif-item-text">{item.body}</div>}
                <div class="notif-item-age">
                  {item.actorName && <span>{item.actorName} · </span>}
                  {age(item.at, now)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function initNotificationBell(): void {
  mountIsland(Islands.notificationBell, NotificationBell, { unread: 0 });
}
